import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendServerBroadcastAPI, fetchServerBroadcastHistoryAPI, getServerDetailsAPI } from '../services/serverService';

// Firebase Storage (Ensure 'storage' is exported in your firebase.js file)
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase'; 

const BroadcastHubPage = () => {
    const { serverId } = useParams();
    const { currentUser } = useAuth();

    const [serverName, setServerName] = useState('Loading...');
    const [history, setHistory] = useState([]);
    const [isFetching, setIsFetching] = useState(true);
    const [isSending, setIsSending] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        audience: 'All Staff',
        requireReadReceipt: false,
    });
    const [attachedFile, setAttachedFile] = useState(null);

    const AUDIENCE_OPTIONS = ['All Staff', 'Employees Only', 'Admins Only', 'Service Users Only', 'All Users'];

    // --- DATA FETCHING ---
    const fetchDashboardData = async () => {
        try {
            setIsFetching(true);
            const [serverRes, historyRes] = await Promise.all([
                getServerDetailsAPI(serverId),
                fetchServerBroadcastHistoryAPI(serverId)
            ]);
            setServerName(serverRes.data.data.companyName);
            setHistory(historyRes.data.data || []);
        } catch (error) {
            console.error("Failed to load broadcast hub data", error);
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        if (serverId) fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverId]);

    // --- HANDLERS ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setAttachedFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.message.trim()) return alert("Message cannot be empty.");

        const confirmSend = window.confirm(`Are you sure you want to broadcast this to ${formData.audience}?`);
        if (!confirmSend) return;

        setIsSending(true);

        try {
            let documentUrl = "";

            // 1. Upload File if it exists
            if (attachedFile) {
                const fileRef = ref(storage, `admins/${currentUser.uid}/broadcasts/${Date.now()}_${attachedFile.name.replace(/\s+/g, '_')}`);
                await uploadBytes(fileRef, attachedFile);
                documentUrl = await getDownloadURL(fileRef);
            }

            // 2. Send the Payload to the Backend
            const payload = {
                serverId,
                title: formData.title.trim() || 'Broadcast Announcement',
                message: formData.message.trim(),
                audience: formData.audience,
                documentUrl: documentUrl,
                requireReadReceipt: formData.requireReadReceipt
            };

            await sendServerBroadcastAPI(payload);

            // 3. Reset form and refresh history
            setFormData({ title: '', message: '', audience: 'All Staff', requireReadReceipt: false });
            setAttachedFile(null);
            document.getElementById('file-upload').value = ""; // Clear file input
            alert("Broadcast sent successfully!");
            
            fetchDashboardData(); 

        } catch (error) {
            console.error("Broadcast failed:", error);
            alert("Failed to send broadcast. Check console for details.");
        } finally {
            setIsSending(false);
        }
    };

    // --- UI COMPONENTS ---
    const renderHistoryCard = (broadcast) => {
        const dateObj = new Date(broadcast.sendAt);
        const formattedDate = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        const formattedTime = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

        return (
            <div key={broadcast.id} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', marginBottom: '15px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                        <h4 style={{ margin: '0 0 5px 0', color: '#2d3748', fontSize: '16px' }}>{broadcast.title}</h4>
                        <div style={{ fontSize: '12px', color: '#718096' }}>Sent by <strong>{broadcast.createdBy}</strong> on {formattedDate} at {formattedTime}</div>
                    </div>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {(broadcast.audiences || []).map((aud, i) => (
                            <span key={i} style={{ backgroundColor: '#ebf8ff', color: '#2b6cb0', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', border: '1px solid #bee3f8' }}>
                                {aud}
                            </span>
                        ))}
                    </div>
                </div>
                
                <div style={{ backgroundColor: '#f7fafc', padding: '15px', borderRadius: '6px', fontSize: '14px', color: '#4a5568', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                    {broadcast.message}
                </div>

                {broadcast.document && (
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #edf2f7' }}>
                        <a href={broadcast.document} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', backgroundColor: '#edf2f7', color: '#4a5568', padding: '8px 12px', borderRadius: '4px', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                            📎 View Attachment
                        </a>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="page-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div className="page-header" style={{ marginBottom: '20px' }}>
                <div>
                    <h1 style={{ margin: 0 }}>Broadcast Hub</h1>
                    <p style={{ margin: '5px 0 0', color: '#718096', fontSize: '14px' }}>Server: <strong style={{ color: '#2b6cb0' }}>{serverName}</strong></p>
                </div>
            </div>
            
            <Link to={`/servers/${serverId}`} className="back-link" style={{ marginBottom: '20px', display: 'inline-block' }}>← Back to Server Details</Link>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px', alignItems: 'start' }}>
                
                {/* LEFT COLUMN: COMPOSE */}
                <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '30px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', borderTop: '5px solid #3182ce' }}>
                    <h2 style={{ fontSize: '18px', margin: '0 0 20px 0', color: '#2d3748', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>📢</span> Compose New Broadcast
                    </h2>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="form-group full-width" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#4a5568' }}>Subject / Title (Optional)</label>
                            <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Mandatory Policy Update" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0' }} />
                        </div>

                        <div className="form-group full-width" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#4a5568' }}>Target Audience</label>
                            <select name="audience" value={formData.audience} onChange={handleChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', backgroundColor: '#f7fafc' }}>
                                {AUDIENCE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                        </div>

                        <div className="form-group full-width" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#4a5568' }}>Message</label>
                            <textarea name="message" value={formData.message} onChange={handleChange} required placeholder="Type your announcement here..." style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e0', minHeight: '150px', resize: 'vertical', fontFamily: 'inherit' }}></textarea>
                        </div>

                        <div style={{ backgroundColor: '#f7fafc', padding: '15px', borderRadius: '6px', border: '1px dashed #a0aec0', marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: 'bold', color: '#4a5568' }}>📎 Attach File or Image (Optional)</label>
                            <input type="file" id="file-upload" onChange={handleFileChange} style={{ fontSize: '14px' }} accept="image/*,.pdf,.doc,.docx" />
                        </div>

                        {/* Note: This is tracked in DB but actual receipt logic belongs to mobile client */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px', padding: '10px', backgroundColor: '#fffaf0', border: '1px solid #feebc8', borderRadius: '6px' }}>
                            <input type="checkbox" id="read-receipt" name="requireReadReceipt" checked={formData.requireReadReceipt} onChange={handleChange} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                            <label htmlFor="read-receipt" style={{ fontSize: '14px', color: '#975a16', cursor: 'pointer', margin: 0, fontWeight: '500' }}>Require clients to acknowledge receipt of this message.</label>
                        </div>

                        <button type="submit" disabled={isSending} style={{ width: '100%', padding: '14px', backgroundColor: '#3182ce', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: 'bold', cursor: isSending ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px rgba(49, 130, 206, 0.2)' }}>
                            {isSending ? 'Sending Broadcast...' : 'Send Broadcast Now'}
                        </button>
                    </form>
                </div>

                {/* RIGHT COLUMN: HISTORY LOG */}
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <h2 style={{ fontSize: '18px', margin: '0 0 20px 0', color: '#4a5568', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span>📜</span> Broadcast History Log
                    </h2>
                    
                    <div style={{ flex: 1, overflowY: 'auto', paddingRight: '10px', maxHeight: '800px' }}>
                        {isFetching ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#718096', backgroundColor: 'white', borderRadius: '8px' }}>Loading history...</div>
                        ) : history.length > 0 ? (
                            history.map(renderHistoryCard)
                        ) : (
                            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#a0aec0', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #cbd5e0' }}>
                                <div style={{ fontSize: '30px', marginBottom: '10px' }}>📭</div>
                                No broadcasts have been sent for this server yet.
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default BroadcastHubPage;